﻿import type { Strings } from './strings.en.js';

/**
 * Portuguese UI strings.
 *
 * European Portuguese, which also reads naturally to Brazilian speakers. Uses "tu"
 * sparingly and prefers impersonal constructions, which keeps the tone friendly without
 * committing to a regional pronoun ("você" vs "tu") that would jar for half the audience.
 *
 * Fixed vocabulary, used consistently:
 *   "limite comum"      the group-safe cap
 *   "máximo confortável" the comfortable maximum
 *   "sala"              the room
 */
export const pt: Strings = {
  common: {
    skipToContent: 'Ir para o conteúdo principal',
    loading: 'A carregar…',
    tryAgain: 'Tentar novamente',
    close: 'Fechar',
    perPerson: 'por pessoa',
    navHowItWorks: 'Como funciona',
    navPrivacy: 'Privacidade',
    navFaq: 'Perguntas',
    navGuides: 'Guias',
    navBlog: 'Artigos',
    homeLink: 'Início',
    language: 'Idioma',
    chooseLanguage: 'Escolher idioma',
    amountInCurrency: (code: string) => `Valor em ${code}`,
    navLandmarkMain: 'Principal',
    navLandmarkFooter: 'Rodapé',
  },

  landing: {
    heroHeadline: 'Combinem um orçamento sem ninguém revelar o seu.',
    heroSupport:
      'Crie um link privado. Cada pessoa indica o máximo que se sentiria confortável a gastar. Só verá o valor com que todo o grupo consegue viver.',
    previewTitle: 'O que o grupo vê',
    previewNote: 'Arredondado para baixo a partir do máximo confortável mais baixo.',
    previewExampleNote: 'Apenas um exemplo. Nenhuma sala foi criada.',
  },

  createForm: {
    legend: 'Criar um orçamento de grupo privado',
    titleLabel: 'Para que é este orçamento?',
    titlePlaceholder: 'Fim de semana em Lisboa',
    titleHint: 'Todas as pessoas com o link veem isto. Não escreva nada privado.',
    peopleLabel: 'Quantas pessoas?',
    peopleHint: 'Entre 3 e 30, incluindo você.',
    currencyLabel: 'Moeda',
    amountLabel: 'O seu máximo confortável por pessoa',
    amountHint: 'O máximo que gastaria à vontade. Não é uma promessa nem um compromisso.',
    submit: 'Criar orçamento privado',
    submitting: 'A criar o seu link privado…',
    trustLine: 'O seu valor nunca é mostrado ao grupo.',
    errorTitleRequired: 'Acrescente uma descrição curta para que se perceba do que se trata.',
    errorTitleTooLong: 'Não ultrapasse os 80 caracteres.',
    errorPeopleRange: 'Escolha entre 3 e 30 pessoas. Três é o mínimo para garantir privacidade.',
    errorAmountRequired: 'Indique o máximo que se sentiria confortável a gastar.',
    errorAmountInvalid: 'Escreva um valor em números, por exemplo 300.',
    errorAmountTooPrecise: 'Esta moeda não usa tantas casas decimais.',
    errorAmountTooLarge: 'Esse valor é demasiado alto.',
    errorGeneric:
      'Algo correu mal ao criar a sala. Os seus dados continuam aqui, por isso pode tentar novamente.',
    errorRateLimited: 'São muitas salas em pouco tempo. Aguarde um minuto e tente novamente.',
    errorTurnstile: 'Não conseguimos verificar este navegador. Recarregue a página e tente outra vez.',
    amountEcho: (formatted: string) => `Ou seja, ${formatted}`,
  },

  share: {
    title: 'O seu link de orçamento privado está pronto.',
    support:
      'Envie-o a todas as pessoas que devem participar. O resultado aparece quando chegarem todas as respostas previstas.',
    linkLabel: 'Link da sala privada',
    copy: 'Copiar link',
    copied: 'Link copiado',
    copyFailed: 'Não foi possível copiar automaticamente. Selecione o link acima e copie-o.',
    share: 'Partilhar link',
    shareTitle: 'Orçamento de grupo privado',
    shareText:
      'Indique o máximo que se sentiria confortável a gastar por pessoa. O seu valor fica privado.',
    manageTitle: 'Guarde o seu link de organizador',
    manageBody:
      'O endereço desta página é também o seu link de organizador. Guarde-o nos favoritos. É a única forma de alterar o tamanho do grupo, reiniciar ou apagar a sala, e não pode ser recuperado.',
    manageWarning: 'Não envie o link de organizador ao grupo. Envie antes o link acima.',
    continue: 'Ir para a sala',
  },

  room: {
    subtitle: 'Orçamento de grupo privado',
    question: 'Qual é o máximo que se sentiria confortável a gastar por pessoa?',
    questionSupport: 'Apenas o limite comum final será partilhado.',
    amountLabel: 'O seu máximo confortável por pessoa',
    submit: 'Enviar em privado',
    submitting: 'A guardar…',
    update: 'Atualizar o meu valor',
    edit: 'Editar o meu valor',
    cancelEdit: 'Cancelar',
    savedTitle: 'Resposta guardada. Pode editá-la até a sala ficar completa.',
    yourAmount: 'O seu valor',
    privacyTitle: 'Privado por conceção',
    privacyPoints: [
      'Sem nome nem e-mail',
      'O seu valor nunca é mostrado',
      'Os valores individuais são apagados após o resultado',
    ],
    privacySentence: 'O seu valor nunca é mostrado ao grupo.',
    progressLabel: (answered: number, expected: number) =>
      `${answered} de ${expected} pessoas já responderam`,
    progressAccessible: (answered: number, expected: number) =>
      `Progresso: ${answered} respostas recebidas de ${expected} previstas. Nenhum nome é registado.`,
    editHint: 'Pode editar a sua resposta até a sala ficar completa.',
    waitingTitle: 'À espera do resto do grupo',
    waitingBody: 'O limite comum aparece aqui assim que todos responderem.',
    refresh: 'Procurar atualizações',
    revealing: 'A calcular o limite comum…',
    fullTitle: 'Este orçamento já está completo.',
    fullBody: 'Não é possível acrescentar mais respostas. Peça o resultado a quem criou a sala.',
    errorSubmit: 'O seu valor não foi guardado. Nada foi enviado ao grupo, por isso tente novamente.',
    errorAmountInvalid: 'Escreva um valor em números, por exemplo 300.',
    errorAmountRequired: 'Indique o máximo que se sentiria confortável a gastar.',
    errorRateLimited: 'Demasiadas tentativas. Aguarde um momento e envie novamente.',
    errorConflict:
      'A sala mudou enquanto escrevia. Já a atualizámos, verifique antes de enviar.',
  },

  result: {
    heading: 'O grupo pode planear com segurança até',
    eyebrow: 'LIMITE COMUM',
    meaningTitle: 'O que isto significa',
    explanation:
      'Este valor é arredondado para baixo e nunca ultrapassa o máximo confortável mais baixo que foi indicado.',
    privacyTitle: 'Privacidade',
    deletion: 'As respostas individuais nunca foram mostradas e já foram apagadas.',
    repeatCta: 'Criar um para o seu grupo',
    footnote: 'Sem nomes · Sem contas · Salas temporárias',
    availableUntil: (date: string) =>
      `Este resultado fica disponível até ${date} e depois é apagado.`,
  },

  manage: {
    title: 'Controlos do organizador',
    subtitle: 'Visíveis apenas para si, a partir do seu link de organizador.',
    peopleLabel: 'Número de pessoas previsto',
    increaseHelp: 'Pode aumentá-lo a qualquer momento antes de o resultado aparecer.',
    decreaseBlocked:
      'Já não pode reduzi-lo agora que outra pessoa respondeu. Reinicie a sala em alternativa.',
    save: 'Atualizar tamanho do grupo',
    saving: 'A atualizar…',
    saved: 'Tamanho do grupo atualizado.',
    resetTitle: 'Reiniciar a sala',
    resetBody: 'Apaga todas as respostas, incluindo a sua, e recomeça do zero.',
    reset: 'Reiniciar sala',
    resetConfirm: 'Reiniciar esta sala? Todas as respostas, incluindo a sua, são apagadas de imediato.',
    resetting: 'A reiniciar…',
    resetDone: 'Sala reiniciada. Todas as respostas foram apagadas.',
    deleteTitle: 'Apagar a sala',
    deleteBody: 'Remove a sala e todas as respostas de imediato. O link deixa de funcionar.',
    delete: 'Apagar sala',
    deleteConfirm:
      'Apagar esta sala definitivamente? Todas as respostas são apagadas de imediato e o link deixa de funcionar.',
    deleting: 'A apagar…',
    deletedTitle: 'Sala apagada.',
    deletedBody: 'A sala e todos os valores enviados foram apagados.',
    errorGeneric: 'Não resultou. Recarregue o seu link de organizador e tente novamente.',
    errorAuth: 'Este link de organizador não é válido para esta sala.',
  },

  states: {
    notFoundTitle: 'Este link não funciona.',
    notFoundBody:
      'A sala pode ter sido apagada por quem a criou, ou o link pode estar incompleto. Verifique o link que recebeu.',
    expiredTitle: 'Esta sala de orçamento privado expirou e os valores enviados foram apagados.',
    expiredBody: 'As salas duram 7 dias. Nada desta é conservado.',
    createNew: 'Criar um novo orçamento',
    genericErrorTitle: 'Algo correu mal.',
    genericErrorBody: 'Não foi possível carregar a sala. Verifique a ligação e tente novamente.',
    offline: 'Parece estar sem ligação.',
  },

  footer: {
    tagline: 'Uma forma discreta de um grupo combinar um único limite de despesa.',
    rights: 'Sem contas. Sem nomes. Salas temporárias.',
    madeBy: (maker: string) => `Feito por ${maker}`,
    groupTrips: 'Viagens em grupo',
    groupGifts: 'Prendas de grupo',
  },

  ads: {
    label: 'Publicidade',
    inactive: (slot: string) => `Espaço publicitário reservado (${slot}), inativo`,
  },
};
